// WHERE YOU ARE, on every screen, from the one list that already knows.
//
// # The measurement this exists for
//
// `/setup` was one screen and is now five addresses. Splitting it moved the MODULE's
// name out of the page title: the `h2` reads "Pozisyonlar" where it used to read
// "Şirketi kurma", so nothing on the screen said which module you were in except an
// uppercase heading in a sidebar that only exists above `lg` and that somebody can
// collapse.
//
// # ONE PLACE, and "on every screen" is STRUCTURAL rather than a checklist
//
// The first version of this drew a trail on two screens out of thirteen, and the reason
// it looked hand-built is that its output was: a rule inside it decided some screens did
// not qualify, and from the outside that is indistinguishable from somebody having gone
// screen by screen and stopped.
//
// So there is no such rule now. This renders whatever `trailOf` derives from
// `destinations.ts` — the one declaration the rail, the command palette, the
// reachability test and the module sidebar already read, whose own comment records that
// those were three lists once, "which is how a screen gets added to one and not the
// others". A breadcrumb assembled per screen would be that same shape a fourth time.
//
// No screen passes crumbs. `Page` draws this, every screen goes through `Page`, and
// `every route the router serves has a trail` fails the build for an address the
// declaration does not place — so "did you remember this screen" stops being a question
// anybody has to answer from memory.
//
// # The trail is the ANCESTORS; the page's own `h2` is the leaf
//
// Argued in full where the rule lives, in `trailOf`. In short: ending the trail on the
// page you are standing on prints the same word twice on almost every screen in this
// product — `people.heading` and `nav.people` are both "Kişiler" — six pixels apart at
// two sizes, which is what makes a header look padded. The `h2` never stopped saying
// which screen this is; what went missing was the level above it.
//
// One consequence, deliberate rather than forgotten: nothing in the list is the current
// page, so nothing carries `aria-current="page"`. The WAI-ARIA breadcrumb pattern
// conditions that attribute on the trail containing a link to the current page, and here
// the last link never is one.
//
// # The markup is the library's
//
// `<nav>` with an accessible name, an ordered list inside it, and separators that are
// `aria-hidden` — without which a screen reader reads the glyph between every level.
// shadcn's breadcrumb carries all of that, so it is composed rather than re-written: the
// aria wiring is precisely what a hand-rolled version loses silently.

import { Fragment } from "react";
import { Link } from "react-router";
import {
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  Breadcrumb as BreadcrumbRoot,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { useT } from "../shell/i18n";
import { useModuleNav } from "./module-nav";

export function Breadcrumb() {
  const t = useT();
  const { trail } = useModuleNav();

  // EMPTY ONLY WHERE THERE IS NO PLACE. Outside the shell, and on the not-found screen —
  // an address riksu does not have is not somewhere it can say you are inside. Every
  // address the router serves resolves, and a test fails the build if one stops.
  if (trail.length === 0) {
    return null;
  }

  return (
    // Named by the catalogue rather than by the library's English default. The generated
    // component hard-codes `aria-label="breadcrumb"`, which is a word riksu's readers do
    // not use and a language this product does not ship.
    <BreadcrumbRoot aria-label={t("nav.trail")}>
      <BreadcrumbList>
        {trail.map((crumb, index) => (
          <Fragment key={crumb.label}>
            {/* DECORATION, and marked as such. The generated separator is a
                `role="presentation"` list item carrying `aria-hidden`, so the glyph is
                not read out — a "/" inside the accessible name is announced as "slash"
                between every level. */}
            {index > 0 ? <BreadcrumbSeparator /> : null}
            {/* THE SIZE GOES ON THE ITEM, not on the list, and that is a measurement
                rather than a preference. `BreadcrumbList` carries the library's
                `text-sm`; passing `text-shell-caption` beside it does NOTHING, because
                tailwind-merge does not recognise riksu's name as a font size and drops
                neither, and in the built stylesheet `.text-sm` lands at byte 27744
                against `.text-shell-caption` at 27108 — later wins, so the override
                loses silently. Applied HERE it is a declaration on the element rather
                than a value inherited from its parent, and a directly-applied
                declaration beats inheritance whatever the source order says. */}
            <BreadcrumbItem className="text-shell-caption">
              {crumb.to === undefined ? (
                // A LEVEL THAT IS NOT A PLACE, and a bare `<span>` on purpose. The group
                // is real — the rail is built out of it — and no page lists one, so it is
                // a word rather than a link. Which crumb is which comes from whether the
                // declaration gave it an address, never from its position in the list.
                //
                // NOT `BreadcrumbPage`, which the tests caught twice. It is a span
                // carrying THREE attributes, and all three are false here:
                // `role="link"` publishes it to the accessibility tree as a link, so it
                // lands in the link list a screen reader offers and follows nowhere;
                // `aria-disabled="true"` softens that to "disabled link", which is a
                // control that refuses rather than no control; and `aria-current="page"`
                // says this IS the page being looked at, which no crumb in an
                // ancestors-only trail ever is. `BreadcrumbPage` is the right component
                // for a trail that ends on its own page — see `trailOf` for why this one
                // does not — and turning off everything it does to borrow its two classes
                // would be using it as a `<span>` with a misleading name on it.
                <span>{t(crumb.label)}</span>
              ) : (
                <BreadcrumbLink
                  render={
                    <Link to={crumb.to} viewTransition>
                      {t(crumb.label)}
                    </Link>
                  }
                />
              )}
            </BreadcrumbItem>
          </Fragment>
        ))}
      </BreadcrumbList>
    </BreadcrumbRoot>
  );
}
